import Handsontable from 'handsontable';
import { CellValue } from 'handsontable/common';

// Handsontable invokes validators with the cell properties as `this`.
interface ValidatorContext {
  instance: Handsontable;
  row: number;
}

// An unticked checkbox reads as `false`, which must not count as the user having entered data.
// Handsontable's own empty check treats `false` as a value, so `minSpareRows` would keep adding
// blank rows on every edit unless grids pass this as their `isEmptyRow`.
export function isHedgePlanRowEmpty(rowData: CellValue[]): boolean {
  return rowData.every((cell) => cell === null || cell === undefined || cell === '' || cell === false);
}

export function isHedgePlanSourceRowEmpty(rowData: object | undefined): boolean {
  return !rowData || isHedgePlanRowEmpty(Object.values(rowData) as CellValue[]);
}

function isRowEmpty(context: ValidatorContext): boolean {
  // The first column is the hidden database ID. It must survive editing so the backend can
  // identify updates, but it must not stop a visibly cleared row from being treated as deleted.
  return isHedgePlanRowEmpty(context.instance.getDataAtRow(context.row).slice(1));
}

export function percentValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  if (value === null || value === undefined || value === '') {
    callback(false);
    return;
  }

  const number = parseFloat(value as string);
  callback(!isNaN(number) && number >= 0 && number <= 100);
}

export function dateValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  if (value === null || value === undefined || value === '') {
    callback(false);
    return;
  }

  const parsed = new Date(value as string);
  callback(!isNaN(parsed.getTime()));
}

// The matrix only produces decile values at ten-point steps, so anything between them would plot nothing.
export function decilePercentileValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  if (value === null || value === undefined || value === '') {
    callback(false);
    return;
  }

  const number = parseFloat(value as string);
  callback(!isNaN(number) && number >= 10 && number <= 90 && number % 10 === 0);
}

const EXECUTION_TYPES = ['Value', 'Budget', 'Time'];

export function executionTypeValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  callback(EXECUTION_TYPES.includes(value as string));
}

// A budget trigger is an absolute price, so it only has to be a number.
export function priceValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  if (value === null || value === undefined || value === '') {
    callback(false);
    return;
  }

  callback(!isNaN(parseFloat(value as string)));
}

// A trade volume in the product's unit: any non-negative number.
export function volumeValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  const volume = parseFloat(value as string);
  callback(value !== null && value !== undefined && value !== '' && !isNaN(volume) && volume >= 0);
}

// A whole number of days; negative is before the season starts.
export function dayCountValidator(
  this: ValidatorContext,
  value: CellValue,
  callback: (valid: boolean) => void,
) {
  if (isRowEmpty(this)) {
    callback(true);
    return;
  }

  callback(value !== null && value !== undefined && value !== '' && Number.isInteger(Number(value)));
}

Handsontable.validators.registerValidator('percentValidator', percentValidator);
Handsontable.validators.registerValidator('dayCountValidator', dayCountValidator);
Handsontable.validators.registerValidator('volumeValidator', volumeValidator);
Handsontable.validators.registerValidator('dateValidator', dateValidator);
Handsontable.validators.registerValidator('decilePercentileValidator', decilePercentileValidator);
Handsontable.validators.registerValidator('priceValidator', priceValidator);
Handsontable.validators.registerValidator('executionTypeValidator', executionTypeValidator);
